/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useContext } from "react";
import { Modal, ModalOverlay, ModalContent, ModalBody } from "@chakra-ui/modal";
import { Button } from "@chakra-ui/react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { MdOutlineContentCopy } from "react-icons/md";
import { ProfileContext } from "../../../store/ProfileProvider";

dayjs.extend(utc);
dayjs.extend(timezone);

const ReceiverAccountDetailsModal: React.FC<{
  detail: any;
  isOpen: boolean;
  onClose(): void;
  receiverId?: string;
}> = ({ isOpen, onClose, detail, receiverId }) => {
  const { profileTimeZone } = useContext(ProfileContext);
  const [receiverAccountDetail, setReceiverAccountDetail] =
    useState<any>(detail);
  const [copied, setCopied] = useState(false);
  const [confirmUrlCopied, setConfirmUrlCopied] = useState(false);
  const userRole = localStorage.getItem("userRole")?.toLowerCase();

  const handleCopyId = async (id: string) => {
    try {
      await navigator.clipboard.writeText(id);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy: ", err);
    }
  };

  const handleCopyConfirmUrl = async (confirmRef: string) => {
    try {
      const url = `${window.location.origin}/confirm/${confirmRef}`;
      await navigator.clipboard.writeText(url);
      setConfirmUrlCopied(true);
      setTimeout(() => setConfirmUrlCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy confirm URL: ", err);
    }
  };

  const closeModal = async () => {
    onClose();
    setReceiverAccountDetail(null);
  };

  const fieldsRendering = (fields: Record<string, any>) => {
    const renderInput = (fieldName: string, fieldValue: any) => {
      const capitalizeLabel = (label: string) => {
        let modifiedLabel = label
          .split("_")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ");

        switch (label) {
          case "bsb":
            modifiedLabel = "BSB";
            break;
          case "bic":
            modifiedLabel = "BIC";
            break;
          case "iban":
            modifiedLabel = "IBAN";
            break;
        }

        return modifiedLabel;
      };

      return (
        <div className="flex" key={fieldName}>
          <div className="w-32 shrink-0 text-gray-700 dark:text-gray-400">
            {capitalizeLabel(fieldName)}
          </div>
          <div className="font-medium tracking-tight text-gray-900 dark:text-white">
            {fieldValue}
          </div>
        </div>
      );
    };

    return Object.entries(fields).map(([key, value]) =>
      renderInput(key, value)
    );
  };

  const formatDate = (timestamp: string) => {
    return dayjs(Number(timestamp))
      .tz(profileTimeZone)
      .format("DD MMM YY - hh:mm A");
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} closeOnOverlayClick>
        <ModalOverlay className="bg-[#000] !opacity-30" />
        <ModalContent className="dark:!bg-neutral-900 !z-[1002] !m-auto !w-full max-w-2xl overflow-hidden !rounded-2xl !border !border-gray-200 !bg-white !shadow-2xl dark:!border-white/10 md:!top-[6vh]">
          <ModalBody className="p-0">
            <div className="flex items-start justify-between gap-4 border-b border-gray-100 px-6 py-5 dark:border-white/10 md:px-7">
              <div className="flex items-start gap-3">
                <div className="mt-0.5 rounded-xl bg-primary-50 p-2 text-primary-600 ring-1 ring-primary-100 dark:bg-primary-900/20 dark:text-primary-300 dark:ring-primary-900/30">
                  <svg
                    viewBox="0 0 24 24"
                    className="h-5 w-5"
                    fill="currentColor"
                  >
                    <path d="M13 9h-2V7h2m0 10h-2v-6h2m-1-9A10 10 0 002 12a10 10 0 0010 10 10 10 0 0010-10A10 10 0 0012 2z" />
                  </svg>
                </div>

                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Account details
                  </h2>
                  <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                    View receiver account information
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={closeModal}
                className="inline-flex h-9 w-9 items-center justify-center rounded-3xl border border-gray-200 text-gray-600 transition hover:bg-gray-50 hover:text-gray-800 dark:border-white/10 dark:text-gray-300 dark:hover:bg-white/5 dark:hover:text-white"
                aria-label="Close"
              >
                <svg
                  viewBox="0 0 24 24"
                  className="h-5 w-5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M18 6 6 18" />
                  <path d="M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="mb-3 max-h-[70vh] overflow-y-auto px-6 py-6 md:px-7">
              <div className="space-y-3">
                <details
                  open
                  className="dark:bg-neutral-900/40 group overflow-hidden rounded-xl border border-gray-200 bg-white/70 shadow-sm dark:border-white/10"
                >
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-5 py-4">
                    <div className="flex items-center gap-3">
                      <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-gray-100 text-gray-700 ring-1 ring-gray-200 dark:bg-white/5 dark:text-gray-200 dark:ring-white/10">
                        <svg
                          viewBox="0 0 24 24"
                          className="h-5 w-5"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M12 20s8-4 8-10V6l-8-3-8 3v4c0 6 8 10 8 10Z" />
                        </svg>
                      </span>
                      <div>
                        <div className="text-sm font-semibold text-gray-800 dark:text-white">
                          Account Details
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          Asset, method, and method fields
                        </div>
                      </div>
                    </div>

                    <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-gray-500 transition group-open:rotate-180 dark:text-gray-300">
                      <svg
                        viewBox="0 0 24 24"
                        className="h-5 w-5"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="m6 9 6 6 6-6" />
                      </svg>
                    </span>
                  </summary>

                  <div className="border-t border-gray-100 px-5 py-5 text-sm dark:border-white/10">
                    <div className="flex flex-col gap-3">
                      <div className="flex">
                        <div className="w-32 shrink-0 text-gray-700 dark:text-gray-400">
                          Asset
                        </div>
                        <div className="font-medium tracking-tight text-gray-900 dark:text-white">
                          {receiverAccountDetail.asset}
                        </div>
                      </div>

                      <div className="flex">
                        <div className="w-32 shrink-0 text-gray-700 dark:text-gray-400">
                          Method
                        </div>
                        <div className="font-medium tracking-tight text-gray-900 dark:text-white">
                          {receiverAccountDetail.method_name}
                        </div>
                      </div>

                      <div className="flex flex-col gap-3">
                        {fieldsRendering(receiverAccountDetail.method_details)}
                      </div>
                    </div>
                  </div>
                </details>

                {receiverAccountDetail.geolocation &&
                  receiverAccountDetail.confirmed_at &&
                  userRole === "superadmin" && (
                    <details className="dark:bg-neutral-900/40 group overflow-hidden rounded-2xl border border-gray-200 bg-white/70 shadow-sm dark:border-white/10">
                      <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-5 py-4">
                        <div className="flex items-center gap-3">
                          <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-gray-100 text-gray-700 ring-1 ring-gray-200 dark:bg-white/5 dark:text-gray-200 dark:ring-white/10">
                            <svg
                              viewBox="0 0 24 24"
                              className="h-5 w-5"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <path d="M12 22s8-4 8-10V7l-8-4-8 4v5c0 6 8 10 8 10Z" />
                              <path d="M9 12l2 2 4-4" />
                            </svg>
                          </span>
                          <div>
                            <div className="text-sm font-semibold text-gray-900 dark:text-white">
                              Account confirmation info
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              Confirmed date + geolocation
                            </div>
                          </div>
                        </div>

                        <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-gray-500 transition group-open:rotate-180 dark:text-gray-300">
                          <svg
                            viewBox="0 0 24 24"
                            className="h-5 w-5"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path d="m6 9 6 6 6-6" />
                          </svg>
                        </span>
                      </summary>

                      <div className="border-t border-gray-100 px-5 py-5 text-sm dark:border-white/10">
                        <div className="flex flex-col gap-3">
                          <div className="flex">
                            <div className="w-32 shrink-0 text-gray-600 dark:text-gray-400">
                              Date
                            </div>
                            <div className="font-medium tracking-tight text-gray-900 dark:text-white">
                              {formatDate(receiverAccountDetail.confirmed_at)}
                            </div>
                          </div>

                          <div className="flex flex-col gap-3">
                            {fieldsRendering(receiverAccountDetail.geolocation)}
                          </div>
                        </div>
                      </div>
                    </details>
                  )}

                {receiverAccountDetail.popv_acc_revr_info &&
                  userRole === "superadmin" && (
                    <details className="dark:bg-neutral-900/40 group overflow-hidden rounded-2xl border border-gray-200 bg-white/70 shadow-sm dark:border-white/10">
                      <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-5 py-4">
                        <div className="flex items-center gap-3">
                          <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-gray-100 text-gray-700 ring-1 ring-gray-200 dark:bg-white/5 dark:text-gray-200 dark:ring-white/10">
                            <svg
                              viewBox="0 0 24 24"
                              className="h-5 w-5"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                              <circle cx="12" cy="7" r="4" />
                            </svg>
                          </span>
                          <div>
                            <div className="text-sm font-semibold text-gray-900 dark:text-white">
                              Account receiver info
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              Receiver profile fields
                            </div>
                          </div>
                        </div>

                        <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-gray-500 transition group-open:rotate-180 dark:text-gray-300">
                          <svg
                            viewBox="0 0 24 24"
                            className="h-5 w-5"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path d="m6 9 6 6 6-6" />
                          </svg>
                        </span>
                      </summary>

                      <div className="border-t border-gray-100 px-5 py-5 text-sm dark:border-white/10">
                        <div className="flex flex-col gap-3">
                          {fieldsRendering(
                            receiverAccountDetail.popv_acc_revr_info
                          )}
                        </div>
                      </div>
                    </details>
                  )}

                {receiverAccountDetail.popv_acc_revr_detail &&
                  userRole === "superadmin" && (
                    <details className="dark:bg-neutral-900/40 group overflow-hidden rounded-2xl border border-gray-200 bg-white/70 shadow-sm dark:border-white/10">
                      <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-5 py-4">
                        <div className="flex items-center gap-3">
                          <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-gray-100 text-gray-700 ring-1 ring-gray-200 dark:bg-white/5 dark:text-gray-200 dark:ring-white/10">
                            <svg
                              viewBox="0 0 24 24"
                              className="h-5 w-5"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4Z" />
                              <path d="M6 20c0-2.66 5.33-4 6-4s6 1.34 6 4" />
                            </svg>
                          </span>
                          <div>
                            <div className="text-sm font-semibold text-gray-900 dark:text-white">
                              Account receiver details
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              Additional receiver detail fields
                            </div>
                          </div>
                        </div>

                        <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-gray-500 transition group-open:rotate-180 dark:text-gray-300">
                          <svg
                            viewBox="0 0 24 24"
                            className="h-5 w-5"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path d="m6 9 6 6 6-6" />
                          </svg>
                        </span>
                      </summary>

                      <div className="border-t border-gray-100 px-5 py-5 text-sm dark:border-white/10">
                        <div className="flex flex-col gap-3">
                          {fieldsRendering(
                            receiverAccountDetail.popv_acc_revr_detail
                          )}
                        </div>
                      </div>
                    </details>
                  )}
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 border-t border-gray-100 px-6 py-4 dark:border-white/10 md:px-7">
              {userRole === "superadmin" &&
                receiverAccountDetail?.confirm_ref &&
                receiverAccountDetail?.confirm_status !== "unconfirmed" && (
                  <Button
                    type="button"
                    onClick={() =>
                      handleCopyConfirmUrl(receiverAccountDetail.confirm_ref)
                    }
                    isDisabled={copied || confirmUrlCopied}
                    className="h-10 rounded-lg border border-gray-200 bg-white px-5 text-sm font-semibold text-gray-700 shadow-sm transition hover:bg-gray-50 active:bg-gray-100 disabled:opacity-60 dark:border-white/10 dark:bg-neutral-800 dark:text-white dark:hover:bg-neutral-700"
                  >
                    <MdOutlineContentCopy className="mr-1.5 h-4 w-4" />
                    {confirmUrlCopied ? "URL Copied!" : "Copy Confirm Account URL"}
                  </Button>
                )}
              {receiverAccountDetail?.id && (
                <Button
                  type="button"
                  onClick={() => handleCopyId(receiverAccountDetail.id)}
                  isDisabled={copied || confirmUrlCopied}
                  className="h-10 rounded-lg border border-gray-200 bg-white px-5 text-sm font-semibold text-gray-700 shadow-sm transition hover:bg-gray-50 active:bg-gray-100 disabled:opacity-60 dark:border-white/10 dark:bg-neutral-800 dark:text-white dark:hover:bg-neutral-700"
                >
                  <MdOutlineContentCopy className="mr-1.5 h-4 w-4" />
                  {copied ? "ID Copied!" : "Copy Account ID"}
                </Button>
              )}
              {receiverId && (
                <Button
                  type="button"
                  onClick={() => window.open(`/${userRole}/receiver/${receiverId}`, '_blank')}
                  className="h-10 rounded-lg border border-gray-200 bg-white px-5 text-sm font-semibold text-gray-700 shadow-sm transition hover:bg-gray-50 active:bg-gray-100 disabled:opacity-60 dark:border-white/10 dark:bg-neutral-800 dark:text-white dark:hover:bg-neutral-700"
                >
                  View Receiver
                </Button>
              )}
              <Button
                type="button"
                onClick={closeModal}
                className="h-10 rounded-lg bg-primary-600 px-5 text-sm font-semibold text-white shadow-sm transition hover:bg-primary-500 active:bg-primary-700 disabled:opacity-60"
              >
                Close
              </Button>
            </div>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

export default ReceiverAccountDetailsModal;
